/** Hooks for items within a sharing group: paginated fetching, creation, editing, and deletion. */
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { captureSentryException } from '@/lib/sentry';

const ITEMS_PAGE_SIZE = 20;
import { supabase } from '@/integrations/supabase/client';

import { mapSupabaseError } from '@/lib/errorMapper';
import { assertOnline } from '@/lib/networkGuard';
import { toast } from 'sonner';
import posthog from '@/lib/posthog';
import { getPhotoSource } from '@/lib/photoLibraryTypes';
import type { Database } from '@/integrations/supabase/types';
import {
  fetchLoansWithFallback,
  buildLoansByItemMap,
  synthesiseLoanStub,
} from '@/hooks/useLoanQueryUtils';

export type CirculationMode = Database['public']['Enums']['circulation_policy'];
export type PowerSource = Database['public']['Enums']['power_type'];

export type ItemCondition = Database['public']['Enums']['item_condition'];
export type ItemStatus = Database['public']['Enums']['item_status'];

export interface Item {
  id: string;
  owner_id: string;
  name: string;
  description: string | null;
  photo_url: string | null;
  category: string | null;
  brand: string | null;
  power_source: PowerSource | null;
  circulation_mode: CirculationMode;
  status: string;
  current_holder_user_id: string | null;
  created_at: string;
  updated_at: string;
  is_giving_away: boolean;
  giving_away_set_at: string | null;
  is_frozen: boolean;
}

export interface ItemWithOwner extends Item {
  owner_name?: string | null;
  active_loan?: {
    id: string;
    borrower_id: string;
    borrowed_at: string;
    borrower_name?: string | null;
    due_at?: string | null;
    status?: string | null;
    return_initiated_by?: string | null;
    group_id?: string | null;
    notes?: string | null;
  } | null;
}

/**
 * Fetches items in a group using cursor-based infinite pagination.
 *
 * The pagination cursor is the `created_at` timestamp of the last item on the
 * current page — cheaper than offset pagination for large lists. Owner names
 * and active loan details are resolved in parallel after the main items fetch
 * to keep latency low. If the loans query fails silently but the item's own
 * status indicates `ON_LOAN`, a minimal loan stub is synthesised so the UI
 * still reflects the correct state.
 *
 * Items are linked to groups via the `item_groups` many-to-many junction table.
 *
 * @param groupId - The UUID of the group whose items to fetch.
 * @returns A TanStack `UseInfiniteQueryResult` with pages of {@link ItemWithOwner} objects.
 */
export function useGroupItems(groupId: string) {
  // Paginated fetch — loads pages on demand
  return useInfiniteQuery({
    queryKey: ['items', groupId],
    queryFn: async ({
      pageParam = null,
    }: {
      pageParam: string | null;
    }): Promise<{ items: ItemWithOwner[]; nextCursor: string | null }> => {
      // Get item IDs in this group from item_groups junction table
      const { data: itemGroupLinks, error: linkError } = await supabase
        .from('item_groups')
        .select('item_id')
        .eq('group_id', groupId);

      if (linkError) throw linkError;
      if (!itemGroupLinks || itemGroupLinks.length === 0) return { items: [], nextCursor: null };

      const itemIds = itemGroupLinks.map((ig) => ig.item_id);

      // Get one page of items, using cursor if provided.
      // Frozen items are hidden from group catalogues — they remain in
      // the owner's My Items view but are not visible to other members.
      let query = supabase
        .from('items')
        .select('*')
        .in('id', itemIds)
        .eq('is_frozen', false)
        .order('created_at', { ascending: false })
        .limit(ITEMS_PAGE_SIZE);

      // [LEARN] Cursor pagination: fetch only rows created before the last item on the previous page, so each page picks up where the last left off.
      if (pageParam) {
        query = query.lt('created_at', pageParam);
      }

      const { data: items, error: itemsError } = await query;

      if (itemsError) throw itemsError;
      if (!items || items.length === 0) return { items: [], nextCursor: null };

      // Fetch owner profiles and active loans in parallel
      const ownerIds = [...new Set(items.map((i) => i.owner_id))];
      const itemIdList = items.map((i) => i.id);

      // Fetch in parallel for speed
      const [{ data: ownerProfiles, error: ownerProfilesError }, safeLoans] = await Promise.all([
        supabase.from('profiles').select('user_id, display_name').in('user_id', ownerIds),
        fetchLoansWithFallback(itemIdList),
      ]);

      if (ownerProfilesError) throw ownerProfilesError;

      const ownerMap = new Map(ownerProfiles?.map((p) => [p.user_id, p.display_name]) || []);

      // Get borrower profiles
      const borrowerIds = [...new Set(safeLoans.map((l) => l.borrower_id))];
      let borrowerMap = new Map<string, string | null>();

      if (borrowerIds.length > 0) {
        const { data: borrowerProfiles, error: borrowerProfilesError } = await supabase
          .from('profiles')
          .select('user_id, display_name')
          .in('user_id', borrowerIds);

        if (borrowerProfilesError) throw borrowerProfilesError;
        borrowerMap = new Map(borrowerProfiles?.map((p) => [p.user_id, p.display_name]) || []);
      }

      // Map loans to items — sort so ACTIVE takes priority over other statuses
      // in case of duplicates (e.g. race between proposal and approval).
      const loansByItem = buildLoansByItemMap(safeLoans);

      const result = items.map((item) => {
        const loan = loansByItem.get(item.id);
        // Primary: use joined loan data. Fallback: if no loan found but the
        // item's own status says ON_LOAN, synthesise a minimal active_loan
        // stub so the UI still shows the correct state. This covers cases
        // where the loans query failed silently.
        const activeLoan = loan
          ? {
              id: loan.id,
              borrower_id: loan.borrower_id,
              borrowed_at: loan.borrowed_at,
              borrower_name: borrowerMap.get(loan.borrower_id) || null,
              due_at: loan.due_at || null,
              status: loan.status || null,
              return_initiated_by: loan.return_initiated_by ?? null,
              group_id: loan.group_id ?? null,
              notes: loan.notes ?? null,
            }
          : synthesiseLoanStub(item);
        return {
          ...item,
          circulation_mode: item.circulation_mode as CirculationMode,
          power_source: item.power_source as PowerSource | null,
          owner_name: ownerMap.get(item.owner_id) || null,
          active_loan: activeLoan,
        };
      });

      const nextCursor =
        items.length === ITEMS_PAGE_SIZE ? items[items.length - 1].created_at : null;
      return { items: result, nextCursor };
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    initialPageParam: null as string | null,
    // Skip query until groupId is available
    enabled: !!groupId,
  });
}

/**
 * Creates a new item and immediately links it to the specified group.
 *
 * Two inserts are performed: one into `items` (the item record) and one into
 * `item_groups` (the many-to-many junction). Both `items` and `my-items`
 * query caches are invalidated on success.
 *
 * @returns A TanStack `UseMutationResult`. The mutation accepts item fields including `groupId`.
 * @throws Supabase error if the tier item limit or 30/24h rate limit is exceeded.
 */
export function useCreateItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      groupId,
      name,
      description,
      category,
      brand,
      circulationMode,
      photoUrl,
      powerSource,
    }: {
      groupId: string;
      name: string;
      description?: string;
      category?: string;
      brand?: string | null;
      circulationMode?: CirculationMode;
      photoUrl?: string;
      powerSource?: PowerSource;
    }) => {
      await assertOnline();
      // Atomic item creation + group linking via RPC (single transaction).
      // Cast needed because auto-generated types don't include this RPC yet.
      const { data: itemId, error: rpcError } = await (supabase.rpc as any)(
        'create_item_with_group',
        {
          p_name: name,
          p_group_id: groupId,
          p_description: description || null,
          p_category: category || null,
          p_brand: brand || null,
          p_circulation_mode: circulationMode || 'RETURN_TO_OWNER_REQUIRED',
          p_photo_url: photoUrl || null,
          p_power_source: powerSource || null,
        },
      );

      if (rpcError) throw rpcError;

      // Fetch the created item to return full data for downstream use
      const { data, error } = await supabase
        .from('items')
        .select()
        .eq('id', itemId)
        .single();

      if (error) throw error;

      return data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['items', variables.groupId] });
      queryClient.invalidateQueries({ queryKey: ['my-items'] });
      posthog.capture('item_created', {
        category: variables.category,
        circulation_mode: variables.circulationMode,
        photo_source: getPhotoSource(variables.photoUrl),
      });
    },
    onError: (error) => {
      captureSentryException(error);
      toast.error(mapSupabaseError(error));
    },
  });
}

/**
 * Updates mutable fields on an existing item.
 *
 * Only fields explicitly provided are included in the update payload, so
 * omitted fields are left unchanged. Invalidates all item query caches since
 * an item can appear in multiple groups.
 *
 * @returns A TanStack `UseMutationResult`. The mutation accepts `{ id: string }` plus optional field overrides.
 */
export function useUpdateItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      name,
      description,
      category,
      brand,
      circulationMode,
      photoUrl,
      powerSource,
    }: {
      id: string;
      name?: string;
      description?: string;
      category?: string;
      brand?: string | null;
      circulationMode?: CirculationMode;
      photoUrl?: string;
      powerSource?: PowerSource | null;
    }) => {
      await assertOnline();
      const updates: Record<string, unknown> = {};
      if (name !== undefined) updates.name = name;
      if (description !== undefined) updates.description = description;
      if (category !== undefined) updates.category = category;
      if (brand !== undefined) updates.brand = brand;
      if (circulationMode !== undefined) updates.circulation_mode = circulationMode;
      if (photoUrl !== undefined) updates.photo_url = photoUrl;
      if (powerSource !== undefined) updates.power_source = powerSource;

      const { data, error } = await supabase
        .from('items')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data, variables) => {
      // Invalidate all item queries since item could be in multiple groups
      queryClient.invalidateQueries({ queryKey: ['items'] });
      queryClient.invalidateQueries({ queryKey: ['my-items'] });
      posthog.capture('item_updated', { item_id: variables.id, category: variables.category, photo_source: getPhotoSource(variables.photoUrl) });
    },
    onError: (error) => {
      captureSentryException(error);
      toast.error(mapSupabaseError(error));
    },
  });
}

/**
 * Permanently deletes an item.
 *
 * Associated `item_groups` junction rows are removed automatically via DB
 * CASCADE. Invalidates all item query caches on success.
 *
 * @returns A TanStack `UseMutationResult`. The mutation accepts `{ id: string }`.
 */
export function useDeleteItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      await assertOnline();
      // item_groups rows are deleted via CASCADE
      const { error } = await supabase.from('items').delete().eq('id', id);

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      // Invalidate all item queries since item could have been in multiple groups
      queryClient.invalidateQueries({ queryKey: ['items'] });
      queryClient.invalidateQueries({ queryKey: ['my-items'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['loan-requests'] });
      posthog.capture('item_deleted', { item_id: variables.id });
    },
    onError: (error) => {
      captureSentryException(error);
      toast.error(mapSupabaseError(error));
    },
  });
}
